import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  MarkEmailRead as MarkEmailReadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import { auth } from "config/firebase";
import { sendEmailVerification } from "firebase/auth";


function VerifyEmailPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector((state: any) => state.user.user);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<'success' | 'error'>('success');
  const [isLoading, setLoading] = useState(false);

  const sendEmail = async () => {
    setLoading(true);
    setMsg("");
    try {
      await sendEmailVerification(auth.currentUser!);
      setMsg(`Verification Email sent to ${user.email}`);
      setMsgType('success');
    } catch (error: any) {
      setMsg(error.message);
      setMsgType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          component="h1" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            color: 'primary.main',
            fontWeight: 600
          }}
        >
          <MarkEmailReadIcon color="primary" fontSize={isMobile ? "large" : "inherit"} />
          Email Verification
        </Typography>
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          color="text.primary"
          sx={{ 
            fontWeight: 400,
            mb: 1
          }}
        >
          Hello {user?.displayName || 'User'}!
        </Typography>
        <Typography 
          variant="body1" 
          color="textSecondary"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Please verify your email address to continue
        </Typography>
      </Box>

      {/* Verification Card */}
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              color="text.primary"
              sx={{ mb: 2, lineHeight: 1.6 }}
            >
              You need to verify your email in order to view this page.
            </Typography>
            
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              If you don't see an email, check your spam folder.
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.primary"
              sx={{ mb: 2, fontWeight: 500 }}
            >
              Refresh this page once you've clicked the link in the email.
            </Typography>
            
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Didn't receive an email? Click the button below to resend.
            </Typography>

            {/* Action Button */}
            {isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Sending verification email...
                </Typography>
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={sendEmail}
                startIcon={<RefreshIcon />}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  borderRadius: 2
                }}
              >
                Send Verification Email
              </Button>
            )}

            {/* Message Display */}
            {msg && (
              <Alert 
                severity={msgType}
                sx={{ 
                  mt: 3,
                  borderRadius: 2,
                  textAlign: 'left',
                  '& .MuiAlert-message': {
                    fontSize: '1rem'
                  }
                }}
              >
                {msg}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default VerifyEmailPage;
